import React, { useRef, useState } from 'react';
import { makeStyles, Theme } from '@material-ui/core';
import { CapaQuotationSpecificAnnotationComment } from '@generated/graphql';
import { colorSet, useSnackbar } from '@components/Provider';
import AnnotationControlPopup from './AnnotationControlPopup';
import AnnotationDetailCommentEdit from './AnnotationDetailCommentEdit';
import AnnotationDetailComment from './AnnotationDetailComment';
import AnnotationDeleteDialog from './AnnotationDeleteDialog';
import userViewerApiContext from '../context/useViewerApiContext';

interface AnnotationDetailCommentItemProps {
  annotationId: string;
  comment: CapaQuotationSpecificAnnotationComment;
  isOriginComment?: boolean;
  isMyComment?: boolean;
}

interface AnnotationDetailCommentItemStyleProps {
  isOriginComment: boolean;
}

const AnnotationDetailCommentItem: React.FunctionComponent<AnnotationDetailCommentItemProps> = ({
  annotationId,
  comment,
  isOriginComment = false,
  isMyComment = false
}) => {
  const {
    onDeleteAnnotation,
    onDeleteComment,
    onUpdateComment
  } = userViewerApiContext();
  const [openAnnotationDeleteDialog, setOpenAnnotationDeleteDialog] = useState<
    boolean
  >(false);
  const [openControlPopup, setOpenControlPopup] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const popupRef = useRef<HTMLButtonElement | null>(null);
  const editedComment = useRef<string>('');
  const classes = useStyles({ isOriginComment });
  const { enqueueSnackbar } = useSnackbar();

  const onOpenDeleteAnnotationDialog = () => {
    setOpenAnnotationDeleteDialog(true);
  };

  const deleteAnnotation = async () => {
    if (!onDeleteAnnotation) return;
    try {
      await onDeleteAnnotation(annotationId);
    } catch {
      enqueueSnackbar('주석 삭제에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setOpenAnnotationDeleteDialog(false);
    }
  };

  const deleteComment = async () => {
    if (!onDeleteComment) return;
    try {
      await onDeleteComment({
        annotationId: annotationId,
        commentId: comment.id as string
      });
    } catch {
      enqueueSnackbar('답글 삭제에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setOpenAnnotationDeleteDialog(true);
    }
  };

  const updateComment = async () => {
    if (!onUpdateComment) return;
    try {
      await onUpdateComment({
        annotationId,
        comment: editedComment.current,
        commentId: comment.id as string
      });

      setEditMode(false);
    } catch {
      enqueueSnackbar('주석 수정에 실패했습니다. 다시 시도해주세요.');
    }
  };

  return (
    <>
      <AnnotationDeleteDialog
        open={openAnnotationDeleteDialog}
        onClose={() => setOpenAnnotationDeleteDialog(false)}
        onDelete={deleteAnnotation}
      />
      <AnnotationControlPopup
        open={openControlPopup}
        anchorRef={popupRef}
        onClose={() => setOpenControlPopup(false)}
        onDelete={
          isOriginComment ? onOpenDeleteAnnotationDialog : deleteComment
        }
        onEdit={() => setEditMode(true)}
      />

      <div className={classes.commentContainer}>
        {!editMode ? (
          <AnnotationDetailComment
            comment={comment}
            isMyComment={isMyComment}
            popupRef={popupRef}
            setOpenControlPopup={setOpenControlPopup}
          />
        ) : (
          <AnnotationDetailCommentEdit
            comment={comment}
            editedComment={editedComment}
            onUpdateComment={updateComment}
            setEditMode={setEditMode}
          />
        )}
      </div>
    </>
  );
};

export default AnnotationDetailCommentItem;

const useStyles = makeStyles<Theme, AnnotationDetailCommentItemStyleProps>(
  (theme) => ({
    commentContainer: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      paddingLeft: ({ isOriginComment }) => (!isOriginComment ? 32 : 0),
      gap: '6px'
    },
    authorInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      '& .name-and-time': {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        '& .time': {
          color: colorSet.gray500
        }
      },
      '& .comment-control-button': {
        marginLeft: 'auto'
      }
    },
    commentContent: {
      paddingLeft: '44px',
      paddingRight: '36px',
      '& > p': {
        wordBreak: 'break-all',
        whiteSpace: 'pre-line'
      }
    }
  })
);
